import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Sidebar } from '../../shared/components/sidebar/sidebar';
import { Navbar } from '../../shared/components/navbar/navbar';
import { Toast } from '../../shared/services/toast';
import { Toast as ToastComponent } from '../../shared/components/toast/toast';
import { DeleteBatchModal } from '../../shared/components/delete-batch-modal/delete-batch-modal';
import {
  PreReq as PreReqService,
  PreReqUpload,
  OrganogramRole,
  BusinessClassification,
  PermissionCatalogItem
} from '../../core/services/pre-req';

type Tab = 'organogram' | 'business-documents' | 'permissions';
type RoleForm = { role: string; description: string; level: number; permissions: string[] };
type ClassificationForm = { classification: string; evidenceText: string; referenceText: string };
type PermissionForm = { displayName: string; description: string; code: string; aliasesText: string; category: string };
type DeleteTarget =
  | { kind: 'role'; id: string; label: string }
  | { kind: 'classification'; id: string; uploadId: string; label: string }
  | { kind: 'permission'; id: string; label: string }
  | { kind: 'permissions-bulk'; ids: string[]; label: string };

const EMPTY_ROLE_FORM: RoleForm = { role: '', description: '', level: 0, permissions: [] };
const EMPTY_CLASSIFICATION_FORM: ClassificationForm = { classification: '', evidenceText: '', referenceText: '' };
const EMPTY_PERMISSION_FORM: PermissionForm = {
  displayName: '',
  description: '',
  code: '',
  aliasesText: '',
  category: 'other'
};

const PERMISSION_CATEGORIES = [
  'finance',
  'salary',
  'employees',
  'technical',
  'billing',
  'hr',
  'general',
  'other'
] as const;

@Component({
  selector: 'app-pre-req',
  standalone: true,
  imports: [CommonModule, FormsModule, Sidebar, Navbar, ToastComponent, DeleteBatchModal],
  templateUrl: './pre-req.html',
  styleUrl: './pre-req.css'
})
export class PreReqPage implements OnInit, AfterViewInit, OnDestroy {
  private preReqService = inject(PreReqService);
  private toast = inject(Toast);

  @ViewChild('scrollContainer') private scrollContainerRef?: ElementRef<HTMLElement>;
  @ViewChildren('docSection') private docSectionRefs!: QueryList<ElementRef<HTMLElement>>;
  private docSectionsSub?: Subscription;
  private docSectionObserver?: IntersectionObserver;

  activeDocId = signal<string | null>(null);

  activeTab = signal<Tab>('organogram');

  // Organogram
  organogramUpload = signal<PreReqUpload | null>(null);
  isLoadingOrganogram = signal(true);
  isUploadingOrganogram = signal(false);
  retryingUploadId = signal<string | null>(null);

  isAddingRole = signal(false);
  editingRoleId = signal<string | null>(null);
  roleForm = signal<RoleForm>(EMPTY_ROLE_FORM);

  // Permission selection overlay (shared by the add-role and edit-role cards) — keeps the
  // org-chart lanes a uniform size instead of one card ballooning with an inline checklist.
  showPermissionsOverlay = signal(false);
  permissionOverlaySearch = signal('');

  // Permission catalog
  permissions = signal<PermissionCatalogItem[]>([]);
  selectedPermissionIds = signal<Set<string>>(new Set());
  permissionCategories = PERMISSION_CATEGORIES;
  isLoadingPermissions = signal(true);
  permissionBusy = signal<string | null>(null);
  permissionQuery = signal('');
  permissionStatusFilter = signal<'all' | 'accepted' | 'pending' | 'rejected'>('all');
  isAddingPermission = signal(false);
  editingPermissionId = signal<string | null>(null);
  permissionForm = signal<PermissionForm>(EMPTY_PERMISSION_FORM);

  // Business documents
  businessDocuments = signal<PreReqUpload[]>([]);
  isLoadingBusinessDocuments = signal(true);
  isUploadingBusinessDocuments = signal(false);
  uploadingCount = signal(0);
  selectedBusinessFiles = signal<File[]>([]);

  addingClassificationForUploadId = signal<string | null>(null);
  editingClassificationId = signal<string | null>(null);
  classificationForm = signal<ClassificationForm>(EMPTY_CLASSIFICATION_FORM);

  // Shared delete confirmation
  deleteTarget = signal<DeleteTarget | null>(null);
  isDeleting = signal(false);

  ngOnInit() {
    this.loadOrganogram();
    this.loadBusinessDocuments();
    this.loadPermissions();
  }

  ngAfterViewInit() {
    // The list loads async, so re-observe every time the rendered set of document sections changes.
    this.docSectionsSub = this.docSectionRefs.changes.subscribe(() => this.setupDocSectionObserver());
    this.setupDocSectionObserver();
  }

  ngOnDestroy() {
    this.docSectionObserver?.disconnect();
    this.docSectionsSub?.unsubscribe();
  }

  setTab(tab: Tab) {
    this.activeTab.set(tab);
  }

  /**
   * Scrollspy for the Business Documents left rail: a section counts as "active" once its
   * top has scrolled into the top 30% of the panel, so the highlighted document updates as
   * the reviewer scrolls through classifications, like a docs-page table of contents.
   */
  private setupDocSectionObserver() {
    this.docSectionObserver?.disconnect();
    const sections = this.docSectionRefs.toArray();
    if (sections.length === 0) return;

    this.docSectionObserver = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length === 0) return;
        const topmost = visible.reduce((a, b) => (a.boundingClientRect.top < b.boundingClientRect.top ? a : b));
        const id = topmost.target.getAttribute('data-doc-id');
        if (id) this.activeDocId.set(id);
      },
      { root: this.scrollContainerRef?.nativeElement ?? null, rootMargin: '0px 0px -70% 0px', threshold: 0 }
    );

    for (const section of sections) this.docSectionObserver.observe(section.nativeElement);

    if (!this.activeDocId()) {
      this.activeDocId.set(sections[0].nativeElement.getAttribute('data-doc-id'));
    }
  }

  scrollToDocument(docId: string) {
    this.docSectionRefs
      ?.toArray()
      .find(s => s.nativeElement.getAttribute('data-doc-id') === docId)
      ?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  loadOrganogram() {
    this.isLoadingOrganogram.set(true);
    this.preReqService.getUploads('Organogram', 1, 1).subscribe({
      next: (res) => {
        this.isLoadingOrganogram.set(false);
        const upload = res.data.items[0] ?? null;
        if (upload) {
          upload.roles = (upload.roles ?? []).map(r => ({
            ...r,
            permissions: r.permissions ?? []
          }));
        }
        this.organogramUpload.set(upload);
      },
      error: (err) => {
        this.isLoadingOrganogram.set(false);
        this.toast.show(err?.error?.message || 'Failed to load organogram uploads.', 'error');
      }
    });
  }

  loadPermissions() {
    this.isLoadingPermissions.set(true);
    this.preReqService.listPermissions().subscribe({
      next: (res) => {
        this.isLoadingPermissions.set(false);
        this.permissions.set(res.data ?? []);
        this.clearPermissionSelection();
      },
      error: (err) => {
        this.isLoadingPermissions.set(false);
        this.toast.show(err?.error?.message || 'Failed to load permissions.', 'error');
      }
    });
  }

  loadBusinessDocuments() {
    this.isLoadingBusinessDocuments.set(true);
    this.preReqService.getUploads('BusinessDocument', 1, 100).subscribe({
      next: (res) => {
        this.isLoadingBusinessDocuments.set(false);
        this.businessDocuments.set(res.data.items);
      },
      error: (err) => {
        this.isLoadingBusinessDocuments.set(false);
        this.toast.show(err?.error?.message || 'Failed to load business documents.', 'error');
      }
    });
  }

  /** Re-runs AI processing for a failed upload using the file already on the server — no re-upload. */
  retryUpload(upload: PreReqUpload) {
    this.retryingUploadId.set(upload.id);
    this.preReqService.retryUpload(upload.id).subscribe({
      next: (res) => {
        this.retryingUploadId.set(null);
        const updated = res.data;
        if (updated.type === 'Organogram') {
          this.organogramUpload.set(updated);
        } else {
          this.businessDocuments.update(list => list.map(d => d.id === updated.id ? updated : d));
        }
        if (updated.status === 'Completed') {
          this.toast.show('Retry succeeded.', 'success');
          if (updated.type === 'Organogram') {
            this.loadPermissions();
          }
        } else if (updated.status === 'Failed') {
          this.toast.show(updated.errorMessage || 'Retry failed.', 'error');
        }
      },
      error: (err) => {
        this.retryingUploadId.set(null);
        this.toast.show(err?.error?.message || 'Failed to retry the upload.', 'error');
      }
    });
  }

  onOrganogramFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    this.isUploadingOrganogram.set(true);
    this.preReqService.upload('Organogram', [file]).subscribe({
      next: (res) => {
        this.isUploadingOrganogram.set(false);
        const upload = res.data[0];
        this.organogramUpload.set(upload);
        if (upload.status === 'Completed') {
          this.toast.show(`Extracted ${upload.roles.length} role(s) from the organogram.`, 'success');
          this.loadPermissions();
        } else if (upload.status === 'Failed') {
          this.toast.show(upload.errorMessage || 'Organogram processing failed.', 'error');
        }
      },
      error: (err) => {
        this.isUploadingOrganogram.set(false);
        this.toast.show(err?.error?.message || 'Failed to upload organogram.', 'error');
      }
    });
  }

  /** Stages picked files instead of uploading immediately, so the user can review/add more before proceeding. */
  onBusinessDocumentsSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (files.length === 0) return;

    // Skip anything already staged (same name + size) so picking the folder twice doesn't duplicate it.
    this.selectedBusinessFiles.update(existing => {
      const isDuplicate = (f: File) => existing.some(e => e.name === f.name && e.size === f.size);
      return [...existing, ...files.filter(f => !isDuplicate(f))];
    });
  }

  removeSelectedBusinessFile(index: number) {
    this.selectedBusinessFiles.update(files => files.filter((_, i) => i !== index));
  }

  clearSelectedBusinessFiles() {
    this.selectedBusinessFiles.set([]);
  }

  proceedBusinessUpload() {
    const files = this.selectedBusinessFiles();
    if (files.length === 0) return;

    this.isUploadingBusinessDocuments.set(true);
    this.uploadingCount.set(files.length);

    this.preReqService.upload('BusinessDocument', files).subscribe({
      next: (res) => {
        this.isUploadingBusinessDocuments.set(false);
        this.selectedBusinessFiles.set([]);
        this.businessDocuments.update(list => [...res.data, ...list]);
        const failed = res.data.filter(d => d.status === 'Failed').length;
        this.toast.show(
          failed > 0
            ? `Classified ${res.data.length - failed} of ${res.data.length} document(s); ${failed} failed.`
            : `Classified ${res.data.length} document(s).`,
          failed > 0 ? 'error' : 'success');
      },
      error: (err) => {
        this.isUploadingBusinessDocuments.set(false);
        this.toast.show(err?.error?.message || 'Failed to upload documents.', 'error');
      }
    });
  }

  formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  badgeClass(status: PreReqUpload['status']): string {
    switch (status) {
      case 'Completed':
      case 'Uploaded':
        return 'badge-completed';
      case 'Processing':
        return 'badge-processing';
      case 'Failed':
        return 'badge-failed';
      default:
        return 'badge-default';
    }
  }

  reviewLabel(review: boolean | null): string {
    if (review === true) return 'Accepted';
    if (review === false) return 'Rejected';
    return 'Pending review';
  }

  reviewBadgeClass(review: boolean | null): string {
    if (review === true) return 'badge-completed';
    if (review === false) return 'badge-failed';
    return 'badge-default';
  }

  /** Accepted/pending/rejected counts across a list of review states, for the stat-pill rows. */
  private reviewStats(reviews: (boolean | null)[]): { total: number; accepted: number; pending: number; rejected: number } {
    return {
      total: reviews.length,
      accepted: reviews.filter(r => r === true).length,
      pending: reviews.filter(r => r === null).length,
      rejected: reviews.filter(r => r === false).length
    };
  }

  roleStats() {
    return this.reviewStats((this.organogramUpload()?.roles ?? []).map(r => r.review));
  }

  permissionStats() {
    return this.reviewStats(this.permissions().map(p => p.review));
  }

  filteredPermissions = computed(() => {
    const query = this.permissionQuery().trim().toLowerCase();
    const status = this.permissionStatusFilter();
    return this.permissions().filter(item => {
      if (status === 'accepted' && item.review !== true) return false;
      if (status === 'pending' && item.review !== null) return false;
      if (status === 'rejected' && item.review !== false) return false;
      if (!query) return true;
      const haystack = [item.displayName, item.code, item.category, item.description, ...(item.aliases ?? [])]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  });

  permissionGroups = computed(() => {
    const groups = new Map<string, PermissionCatalogItem[]>();
    for (const item of this.filteredPermissions()) {
      const category = item.category || 'other';
      const list = groups.get(category) ?? [];
      list.push(item);
      groups.set(category, list);
    }
    return Array.from(groups.entries()).map(([category, items]) => ({ category, items }));
  });

  /** Accepted catalog entries available for role multi-select. */
  acceptedPermissions = computed(() => this.permissions().filter(p => p.review === true));

  /** Accepted permissions currently checked on `roleForm`, filtered by the overlay search box. */
  selectedOverlayPermissions = computed(() => {
    const selected = new Set(this.roleForm().permissions);
    const query = this.permissionOverlaySearch().trim().toLowerCase();
    return this.acceptedPermissions()
      .filter(p => selected.has(p.code))
      .filter(p => !query || p.displayName.toLowerCase().includes(query) || p.code.toLowerCase().includes(query));
  });

  /** Accepted permissions not yet checked on `roleForm`, filtered by the overlay search box. */
  availableOverlayPermissions = computed(() => {
    const selected = new Set(this.roleForm().permissions);
    const query = this.permissionOverlaySearch().trim().toLowerCase();
    return this.acceptedPermissions()
      .filter(p => !selected.has(p.code))
      .filter(p => !query || p.displayName.toLowerCase().includes(query) || p.code.toLowerCase().includes(query));
  });

  openPermissionsOverlay() {
    if (this.acceptedPermissions().length === 0) return;
    this.permissionOverlaySearch.set('');
    this.showPermissionsOverlay.set(true);
  }

  closePermissionsOverlay() {
    this.showPermissionsOverlay.set(false);
  }

  /** Codes currently attached to at least one organogram role. */
  assignedPermissionCodes = computed(() => {
    const codes = new Set<string>();
    for (const role of this.organogramUpload()?.roles ?? []) {
      for (const code of role.permissions ?? []) {
        const c = (code || '').trim().toLowerCase();
        if (c) codes.add(c);
      }
    }
    return codes;
  });

  /** Catalog permissions not present on any organogram role (red-dot targets). */
  unassignedPermissions = computed(() => {
    const assigned = this.assignedPermissionCodes();
    return this.permissions().filter(p => {
      const code = (p.code || '').trim().toLowerCase();
      return !!code && !assigned.has(code) && p.review !== false;
    });
  });

  unassignedPermissionCount = computed(() => this.unassignedPermissions().length);

  hasUnassignedPermissions = computed(() => this.unassignedPermissionCount() > 0);

  isPermissionUnassigned(item: PermissionCatalogItem): boolean {
    const code = (item.code || '').trim().toLowerCase();
    if (!code || item.review === false) return false;
    return !this.assignedPermissionCodes().has(code);
  }

  selectedPermissionCount = computed(() => this.selectedPermissionIds().size);

  allPermissionsSelected = computed(() => {
    const list = this.permissions();
    if (list.length === 0) return false;
    const selected = this.selectedPermissionIds();
    return list.every(p => selected.has(p.id));
  });

  isPermissionSelected(id: string): boolean {
    return this.selectedPermissionIds().has(id);
  }

  togglePermissionSelected(id: string) {
    const next = new Set(this.selectedPermissionIds());
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this.selectedPermissionIds.set(next);
  }

  toggleSelectAllPermissions() {
    if (this.allPermissionsSelected()) {
      this.selectedPermissionIds.set(new Set());
      return;
    }
    this.selectedPermissionIds.set(new Set(this.permissions().map(p => p.id)));
  }

  clearPermissionSelection() {
    this.selectedPermissionIds.set(new Set());
  }

  bulkAcceptSelected() {
    const ids = [...this.selectedPermissionIds()];
    if (ids.length === 0 || this.permissionBusy()) return;
    this.permissionBusy.set('Accepting permissions…');
    this.preReqService.bulkReviewPermissions(ids, true).subscribe({
      next: () => {
        this.permissionBusy.set(null);
        this.loadPermissions();
        this.clearPermissionSelection();
        this.toast.show(`Accepted ${ids.length} permission(s).`, 'success');
      },
      error: (err) => {
        this.permissionBusy.set(null);
        this.toast.show(err?.error?.message || 'Bulk accept failed.', 'error');
      }
    });
  }

  bulkRejectSelected() {
    const ids = [...this.selectedPermissionIds()];
    if (ids.length === 0 || this.permissionBusy()) return;
    this.permissionBusy.set('Rejecting permissions…');
    this.preReqService.bulkReviewPermissions(ids, false).subscribe({
      next: () => {
        this.permissionBusy.set(null);
        this.loadPermissions();
        this.clearPermissionSelection();
        this.toast.show(`Rejected ${ids.length} permission(s).`, 'success');
      },
      error: (err) => {
        this.permissionBusy.set(null);
        this.toast.show(err?.error?.message || 'Bulk reject failed.', 'error');
      }
    });
  }

  confirmBulkDeleteSelected() {
    const ids = [...this.selectedPermissionIds()];
    if (ids.length === 0) return;
    this.deleteTarget.set({
      kind: 'permissions-bulk',
      ids,
      label: `${ids.length} selected permission(s)`
    });
  }

  permissionLabel(code: string): string {
    const match = this.permissions().find(p => p.code === code);
    return match ? `${match.displayName} (${match.code})` : code;
  }

  toggleRolePermission(code: string) {
    const current = this.roleForm().permissions;
    const next = current.includes(code)
      ? current.filter(c => c !== code)
      : [...current, code];
    this.roleForm.set({ ...this.roleForm(), permissions: next });
  }

  isRolePermissionSelected(code: string): boolean {
    return this.roleForm().permissions.includes(code);
  }

  classificationStats() {
    return this.reviewStats(this.businessDocuments().flatMap(d => d.classifications).map(c => c.review));
  }

  /** Cycles a small palette across tiers so levels read apart at a glance without needing an avatar. */
  levelColorClass(level: number): string {
    const palette = ['lvl-0', 'lvl-1', 'lvl-2', 'lvl-3'];
    return palette[((level % palette.length) + palette.length) % palette.length];
  }

  fileTypeInfo(fileName: string): { label: string; cssClass: string } {
    const ext = (fileName.split('.').pop() ?? '').toLowerCase();
    if (ext === 'pdf') return { label: 'PDF', cssClass: 'ft-pdf' };
    if (ext === 'doc' || ext === 'docx') return { label: 'DOC', cssClass: 'ft-docx' };
    if (ext === 'xls' || ext === 'xlsx' || ext === 'csv') return { label: 'XLS', cssClass: 'ft-xlsx' };
    return { label: ext ? ext.toUpperCase().slice(0, 4) : 'FILE', cssClass: 'ft-default' };
  }

  // ── Organogram roles ─────────────────────────────────────────────────────────────────

  /**
   * Groups roles into tiers by their level number for the org-chart view. Roles only carry
   * a flat level, not a specific parent, so a tier is centered under the whole tier above
   * rather than under one particular card in it — an honest hierarchy, not a guessed one.
   *
   * A `computed()` rather than a plain method: the template calls this directly in *ngFor,
   * and a plain method would rebuild fresh array/object references on every change-detection
   * tick (e.g. each keystroke in the edit form), causing NgFor to tear down and recreate the
   * whole org-card tree — including whatever input the user is actively typing into. Memoizing
   * keeps the same references (and the same DOM) unless organogramUpload actually changes.
   */
  roleLevels = computed<{ level: number; roles: OrganogramRole[] }[]>(() => {
    const upload = this.organogramUpload();
    if (!upload) return [];
    const byLevel = new Map<number, OrganogramRole[]>();
    for (const role of upload.roles) {
      if (!byLevel.has(role.level)) byLevel.set(role.level, []);
      byLevel.get(role.level)!.push(role);
    }
    return Array.from(byLevel.entries())
      .sort(([a], [b]) => a - b)
      .map(([level, roles]) => ({ level, roles }));
  });

  trackByLevel(_index: number, group: { level: number; roles: OrganogramRole[] }): number {
    return group.level;
  }

  trackByRoleId(_index: number, role: OrganogramRole): string {
    return role.id;
  }

  startAddRole() {
    this.editingRoleId.set(null);
    this.roleForm.set({ ...EMPTY_ROLE_FORM });
    this.isAddingRole.set(true);
  }

  cancelRoleForm() {
    this.isAddingRole.set(false);
    this.editingRoleId.set(null);
    this.showPermissionsOverlay.set(false);
  }

  startEditRole(role: OrganogramRole) {
    this.isAddingRole.set(false);
    this.editingRoleId.set(role.id);
    this.roleForm.set({
      role: role.role,
      description: role.description,
      level: role.level,
      permissions: [...(role.permissions ?? [])]
    });
  }

  saveNewRole() {
    const upload = this.organogramUpload();
    const form = this.roleForm();
    if (!upload) return;
    if (!form.role.trim()) {
      this.toast.show('Role name is required.', 'error');
      return;
    }

    this.preReqService.createRole(
      upload.id, form.role.trim(), form.description.trim(), form.level, form.permissions
    ).subscribe({
      next: (res) => {
        this.organogramUpload.update(u => u ? { ...u, roles: [...u.roles, res.data] } : u);
        this.isAddingRole.set(false);
        this.showPermissionsOverlay.set(false);
        this.toast.show('Role added.', 'success');
      },
      error: (err) => this.toast.show(err?.error?.message || 'Failed to add role.', 'error')
    });
  }

  saveEditRole(roleId: string) {
    const form = this.roleForm();
    if (!form.role.trim()) {
      this.toast.show('Role name is required.', 'error');
      return;
    }

    this.preReqService.updateRole(
      roleId, form.role.trim(), form.description.trim(), form.level, form.permissions
    ).subscribe({
      next: (res) => {
        this.replaceRole(res.data);
        this.editingRoleId.set(null);
        this.showPermissionsOverlay.set(false);
        this.toast.show('Role updated.', 'success');
      },
      error: (err) => this.toast.show(err?.error?.message || 'Failed to update role.', 'error')
    });
  }

  setRoleReview(role: OrganogramRole, review: boolean) {
    this.preReqService.reviewRole(role.id, review).subscribe({
      next: (res) => this.replaceRole(res.data),
      error: (err) => this.toast.show(err?.error?.message || 'Failed to update review.', 'error')
    });
  }

  confirmDeleteRole(role: OrganogramRole) {
    this.deleteTarget.set({ kind: 'role', id: role.id, label: `the role "${role.role}"` });
  }

  private replaceRole(updated: OrganogramRole) {
    this.organogramUpload.update(u => u ? { ...u, roles: u.roles.map(r => r.id === updated.id ? updated : r) } : u);
  }

  // ── Permission catalog ──────────────────────────────────────────────────────────────

  startAddPermission() {
    this.editingPermissionId.set(null);
    this.permissionForm.set({ ...EMPTY_PERMISSION_FORM });
    this.isAddingPermission.set(true);
  }

  cancelPermissionForm() {
    this.isAddingPermission.set(false);
    this.editingPermissionId.set(null);
  }

  startEditPermission(item: PermissionCatalogItem) {
    this.isAddingPermission.set(false);
    this.editingPermissionId.set(item.id);
    this.permissionForm.set({
      displayName: item.displayName,
      description: item.description,
      code: item.code,
      aliasesText: (item.aliases ?? []).join(', '),
      category: item.category || 'other'
    });
  }

  private parseAliases(text: string): string[] {
    return text.split(',').map(a => a.trim()).filter(a => a.length > 0);
  }

  saveNewPermission() {
    const form = this.permissionForm();
    if (!form.displayName.trim()) {
      this.toast.show('Display name is required.', 'error');
      return;
    }
    if (this.permissionBusy()) return;
    this.permissionBusy.set('Adding permission…');
    this.preReqService.createPermission(
      form.displayName.trim(),
      form.description.trim(),
      form.code.trim() || null,
      this.parseAliases(form.aliasesText),
      form.category || 'other'
    ).subscribe({
      next: (res) => {
        this.permissionBusy.set(null);
        this.permissions.update(list => [...list, res.data]);
        this.isAddingPermission.set(false);
        this.toast.show('Permission added.', 'success');
      },
      error: (err) => {
        this.permissionBusy.set(null);
        this.toast.show(err?.error?.message || 'Failed to add permission.', 'error');
      }
    });
  }

  saveEditPermission(id: string) {
    const form = this.permissionForm();
    if (!form.displayName.trim()) {
      this.toast.show('Display name is required.', 'error');
      return;
    }
    if (this.permissionBusy()) return;
    this.permissionBusy.set('Saving permission…');
    this.preReqService.updatePermission(
      id,
      form.displayName.trim(),
      form.description.trim(),
      form.code.trim() || null,
      this.parseAliases(form.aliasesText),
      form.category || 'other'
    ).subscribe({
      next: (res) => {
        this.permissionBusy.set(null);
        this.permissions.update(list => list.map(p => p.id === res.data.id ? res.data : p));
        this.editingPermissionId.set(null);
        this.toast.show('Permission updated.', 'success');
      },
      error: (err) => {
        this.permissionBusy.set(null);
        this.toast.show(err?.error?.message || 'Failed to update permission.', 'error');
      }
    });
  }

  setPermissionReview(item: PermissionCatalogItem, review: boolean) {
    if (this.permissionBusy()) return;
    this.permissionBusy.set(review ? 'Accepting permission…' : 'Rejecting permission…');
    this.preReqService.reviewPermission(item.id, review).subscribe({
      next: (res) => {
        this.permissionBusy.set(null);
        this.permissions.update(list => list.map(p => p.id === res.data.id ? res.data : p));
      },
      error: (err) => {
        this.permissionBusy.set(null);
        this.toast.show(err?.error?.message || 'Failed to update review.', 'error');
      }
    });
  }

  confirmDeletePermission(item: PermissionCatalogItem) {
    this.deleteTarget.set({ kind: 'permission', id: item.id, label: `the permission "${item.displayName}"` });
  }

  trackByPermissionId(_index: number, item: PermissionCatalogItem): string {
    return item.id;
  }

  // ── Business classifications ────────────────────────────────────────────────────────

  startAddClassification(uploadId: string) {
    this.editingClassificationId.set(null);
    this.classificationForm.set({ ...EMPTY_CLASSIFICATION_FORM });
    this.addingClassificationForUploadId.set(uploadId);
  }

  cancelClassificationForm() {
    this.addingClassificationForUploadId.set(null);
    this.editingClassificationId.set(null);
  }

  startEditClassification(cls: BusinessClassification) {
    this.addingClassificationForUploadId.set(null);
    this.editingClassificationId.set(cls.id);
    this.classificationForm.set({
      classification: cls.classification,
      evidenceText: cls.evidenceText,
      referenceText: cls.referenceText ?? ''
    });
  }

  saveNewClassification(uploadId: string) {
    const form = this.classificationForm();
    if (!form.classification.trim()) {
      this.toast.show('Classification name is required.', 'error');
      return;
    }

    this.preReqService.createClassification(uploadId, form.classification.trim(), form.evidenceText.trim(), form.referenceText.trim() || null).subscribe({
      next: (res) => {
        this.updateDocument(uploadId, doc => ({ ...doc, classifications: [...doc.classifications, res.data] }));
        this.addingClassificationForUploadId.set(null);
        this.toast.show('Classification added.', 'success');
      },
      error: (err) => this.toast.show(err?.error?.message || 'Failed to add classification.', 'error')
    });
  }

  saveEditClassification(uploadId: string, classificationId: string) {
    const form = this.classificationForm();
    if (!form.classification.trim()) {
      this.toast.show('Classification name is required.', 'error');
      return;
    }

    this.preReqService.updateClassification(classificationId, form.classification.trim(), form.evidenceText.trim(), form.referenceText.trim() || null).subscribe({
      next: (res) => {
        this.replaceClassification(uploadId, res.data);
        this.editingClassificationId.set(null);
        this.toast.show('Classification updated.', 'success');
      },
      error: (err) => this.toast.show(err?.error?.message || 'Failed to update classification.', 'error')
    });
  }

  setClassificationReview(uploadId: string, cls: BusinessClassification, review: boolean) {
    this.preReqService.reviewClassification(cls.id, review).subscribe({
      next: (res) => this.replaceClassification(uploadId, res.data),
      error: (err) => this.toast.show(err?.error?.message || 'Failed to update review.', 'error')
    });
  }

  confirmDeleteClassification(uploadId: string, cls: BusinessClassification) {
    this.deleteTarget.set({ kind: 'classification', id: cls.id, uploadId, label: `the classification "${cls.classification}"` });
  }

  private replaceClassification(uploadId: string, updated: BusinessClassification) {
    this.updateDocument(uploadId, doc => ({
      ...doc,
      classifications: doc.classifications.map(c => c.id === updated.id ? updated : c)
    }));
  }

  private updateDocument(uploadId: string, updater: (doc: PreReqUpload) => PreReqUpload) {
    this.businessDocuments.update(list => list.map(d => d.id === uploadId ? updater(d) : d));
  }

  // ── Shared delete confirmation ───────────────────────────────────────────────────────

  cancelDelete() {
    if (this.isDeleting()) return;
    this.deleteTarget.set(null);
  }

  confirmDelete() {
    const target = this.deleteTarget();
    if (!target) return;
    this.isDeleting.set(true);

    if (target.kind === 'role') {
      this.preReqService.deleteRole(target.id).subscribe({
        next: () => {
          this.organogramUpload.update(u => u ? { ...u, roles: u.roles.filter(r => r.id !== target.id) } : u);
          this.isDeleting.set(false);
          this.deleteTarget.set(null);
          this.toast.show('Role deleted.', 'success');
        },
        error: (err) => {
          this.isDeleting.set(false);
          this.toast.show(err?.error?.message || 'Failed to delete role.', 'error');
        }
      });
    } else if (target.kind === 'permission') {
      this.permissionBusy.set('Deleting permission…');
      this.preReqService.deletePermission(target.id).subscribe({
        next: () => {
          this.permissionBusy.set(null);
          this.permissions.update(list => list.filter(p => p.id !== target.id));
          this.selectedPermissionIds.update(s => {
            const next = new Set(s);
            next.delete(target.id);
            return next;
          });
          this.isDeleting.set(false);
          this.deleteTarget.set(null);
          this.toast.show('Permission deleted.', 'success');
        },
        error: (err) => {
          this.permissionBusy.set(null);
          this.isDeleting.set(false);
          this.toast.show(err?.error?.message || 'Failed to delete permission.', 'error');
        }
      });
    } else if (target.kind === 'permissions-bulk') {
      this.permissionBusy.set('Deleting permissions…');
      this.preReqService.bulkDeletePermissions(target.ids).subscribe({
        next: () => {
          this.permissionBusy.set(null);
          const idSet = new Set(target.ids);
          this.permissions.update(list => list.filter(p => !idSet.has(p.id)));
          this.clearPermissionSelection();
          this.isDeleting.set(false);
          this.deleteTarget.set(null);
          this.toast.show(`Deleted ${target.ids.length} permission(s).`, 'success');
        },
        error: (err) => {
          this.permissionBusy.set(null);
          this.isDeleting.set(false);
          this.toast.show(err?.error?.message || 'Failed to bulk-delete permissions.', 'error');
        }
      });
    } else {
      this.preReqService.deleteClassification(target.id).subscribe({
        next: () => {
          this.updateDocument(target.uploadId, doc => ({
            ...doc,
            classifications: doc.classifications.filter(c => c.id !== target.id)
          }));
          this.isDeleting.set(false);
          this.deleteTarget.set(null);
          this.toast.show('Classification deleted.', 'success');
        },
        error: (err) => {
          this.isDeleting.set(false);
          this.toast.show(err?.error?.message || 'Failed to delete classification.', 'error');
        }
      });
    }
  }
}
